import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BILLING_LABEL, billingFor, calcQuote, type SizingThresholds } from '@veta-desk/domain';
import { api, type QuoteDraft, type Ticket } from '../lib/api';
import { dateTime, relativeTime } from '../lib/format';

// Estimado (consultor) y cotización (solo administrador) de un REQ.
// El consultor nunca ve la cotización: la API no se la manda.

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const area = 'w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 py-2 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';

const money = (n: number, currency: string) => {
  try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(n); } catch { return `${n.toFixed(2)} ${currency}`; }
};

export function EstimateQuote({ ticket, sizing, isAdmin, quoteExpired, onChanged, onError }: {
  ticket: Ticket; sizing: SizingThresholds | undefined; isAdmin: boolean; quoteExpired: boolean;
  onChanged: (msg: string) => void; onError: (e: unknown) => void;
}) {
  const est = ticket.estimate;
  const canEstimate = ['new', 'assigned', 'estimating'].includes(ticket.status);
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState(est ? String(est.hours) : '');
  const [notes, setNotes] = useState(est?.notes ?? '');

  const submit = useMutation({
    mutationFn: () => api(`/tickets/${ticket.number}/estimate`, { method: 'POST', body: JSON.stringify({ hours: Number(hours), notes }) }),
    onSuccess: () => { setEditing(false); onChanged('Estimado registrado; queda por cotizar.'); },
    onError
  });

  const status = (() => {
    if (!est) return null;
    if (ticket.status === 'pending_approval') return { tone: 'amber', text: `Cotización enviada${ticket.quote ? ` ${relativeTime(ticket.quote.sentAt)}` : ''}; esperando al cliente${quoteExpired ? ' · vencida' : ''}` };
    if (est.status === 'approved') return { tone: 'green', text: `Aprobado por ${est.approvedBy ?? 'el cliente'} · ${dateTime(est.decidedAt)}` };
    if (ticket.status === 'rejected') return { tone: 'red', text: 'Rechazado por el cliente' };
    if (est.status === 'rejected') return { tone: 'red', text: 'El cliente pidió ajustes: actualiza el estimado' };
    return { tone: 'amber', text: `Registrado ${relativeTime(est.submittedAt)} por ${est.submittedBy.name}; por cotizar` };
  })();
  const tone = { green: 'bg-accent-soft text-accent-dark', red: 'bg-[#FBEAEA] text-[#8F1D1D]', amber: 'bg-[#FFF4E5] text-[#6A3A00]' };

  return (
    <div className="flex flex-col gap-3 text-sm">
      {est && !editing ? (
        <div>
          <p><span className="font-mono text-2xl font-semibold">{est.hours} h</span>
            <span className="ml-2 text-xs text-muted">estimado del consultor · v{est.version}</span></p>
          {sizing && (() => {
            const billing = billingFor(est.hours, sizing);
            return (
              <p className={`mt-1 text-xs ${billing === 'additional' ? 'font-semibold text-[#9A3412]' : 'text-muted'}`}>
                Preliminar: {est.hours > sizing.reqMaxHours ? `más de ${sizing.reqMaxHours} h, conviértelo a proyecto` : BILLING_LABEL[billing].toLowerCase()}
                {isAdmin ? ' · la definitiva sale del subtotal con riesgo' : ''}
              </p>
            );
          })()}
          {status && <p className={`mt-2 rounded-md px-3 py-2 text-xs ${tone[status.tone as keyof typeof tone]}`}>{status.text}</p>}
          {est.approvalEvidence && <p className="mt-2 text-xs text-muted">Evidencia: {est.approvalEvidence}</p>}
          {est.notes && <p className="mt-2 whitespace-pre-line text-xs">{est.notes}</p>}
        </div>
      ) : !editing && <p className="text-muted">Sin estimado todavía.</p>}

      {editing ? (
        <form className="flex flex-col gap-2" onSubmit={e => { e.preventDefault(); submit.mutate(); }}>
          <label className="text-xs text-muted">Horas estimadas (funcional + técnico)
            <input type="number" min="0.5" step="0.5" required className={`${field} mt-1`} value={hours} onChange={e => setHours(e.target.value)} />
          </label>
          <label className="text-xs text-muted">Detalle
            <textarea rows={3} className={`${area} mt-1`} value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className={btnPrimary} disabled={submit.isPending || !(Number(hours) > 0)}>Registrar</button>
            <button type="button" className={btnSecondary} onClick={() => setEditing(false)}>Cancelar</button>
          </div>
        </form>
      ) : canEstimate && (
        <button type="button" className={btnSecondary} onClick={() => setEditing(true)}>{est ? 'Actualizar estimado' : 'Registrar estimado'}</button>
      )}

      {isAdmin && est && <QuotePanel ticket={ticket} quoteExpired={quoteExpired} onChanged={onChanged} onError={onError} />}
    </div>
  );
}

function QuotePanel({ ticket, quoteExpired, onChanged, onError }: {
  ticket: Ticket; quoteExpired: boolean; onChanged: (msg: string) => void; onError: (e: unknown) => void;
}) {
  const q = ticket.quote;
  const canQuote = ticket.estimate?.status === 'submitted' && ['estimating', 'pending_approval'].includes(ticket.status);
  const [open, setOpen] = useState(false);
  const draft = useQuery({
    queryKey: ['quote-draft', ticket.number, ticket.estimate?.version],
    queryFn: () => api<QuoteDraft>(`/tickets/${ticket.number}/quote`),
    enabled: open
  });
  const [f, setF] = useState({ riskPct: '10', mgmtPct: '10', marginPct: '0', rate: '', clientNotes: '' });
  useEffect(() => {
    if (!draft.data) return;
    const base = q ?? draft.data.factors;
    setF({ riskPct: String(base.riskPct), mgmtPct: String(base.mgmtPct), marginPct: String(base.marginPct),
      rate: draft.data.rate !== null ? String(q?.rate ?? draft.data.rate) : '', clientNotes: q?.clientNotes ?? '' });
  }, [draft.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useMutation({
    mutationFn: () => api(`/tickets/${ticket.number}/quote`, {
      method: 'POST',
      body: JSON.stringify({ riskPct: Number(f.riskPct), mgmtPct: Number(f.mgmtPct), marginPct: Number(f.marginPct),
        rate: f.rate ? Number(f.rate) : null, clientNotes: f.clientNotes })
    }),
    onSuccess: () => { setOpen(false); onChanged('Cotización enviada al cliente.'); },
    onError
  });

  const d = draft.data;
  const preview = d?.estimate ? calcQuote(d.estimate.hours,
    { riskPct: Number(f.riskPct) || 0, mgmtPct: Number(f.mgmtPct) || 0, marginPct: Number(f.marginPct) || 0 },
    f.rate ? Number(f.rate) : null, d.sizing) : null;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(x => ({ ...x, [k]: e.target.value }));

  return (
    <div className="border-t border-line pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">Cotización · solo administrador</p>
      {q && (
        <div className="mt-2">
          <p><span className="font-mono text-xl font-semibold">{q.hours} h</span>
            {q.price !== null && <span className="ml-2 font-semibold text-[#3D6B8A]">{money(q.price, q.currency)}</span>}</p>
          <p className="mt-0.5 text-xs text-muted">
            v{q.version} · subtotal {q.subtotalHours ?? q.hours} h (riesgo {q.riskPct} %) + gestión {q.mgmtHours ?? 0} h ({q.mgmtPct} %) · margen {q.marginPct} %{q.unitPrice ? ` · ${money(q.unitPrice, q.currency)}/h` : ''}
            · vence {dateTime(q.validUntil)}{quoteExpired ? ' (vencida)' : ''}
          </p>
          {q.decision && <p className="mt-1 text-xs">Decisión: {{ approved: 'aprobada', rejected: 'rechazada', changes: 'pidió ajustes' }[q.decision.outcome]} · {q.decision.by}{q.decision.reason ? ` — “${q.decision.reason}”` : ''}</p>}
        </div>
      )}
      {canQuote && !open && (
        <button type="button" className={`${btnPrimary} mt-2`} onClick={() => setOpen(true)}>{q && ticket.status === 'pending_approval' ? 'Actualizar cotización' : 'Cotizar y enviar'}</button>
      )}
      {open && (
        draft.isLoading || !d ? <p className="mt-2 text-xs text-muted">Cargando…</p> : (
          <form className="mt-2 flex flex-col gap-2" onSubmit={e => { e.preventDefault(); send.mutate(); }}>
            <div className="grid grid-cols-3 gap-2">
              <label className="text-xs text-muted">Riesgo %<input type="number" min="0" step="1" className={`${field} mt-1`} value={f.riskPct} onChange={set('riskPct')} /></label>
              <label className="text-xs text-muted">Gestión %<input type="number" min="0" step="1" className={`${field} mt-1`} value={f.mgmtPct} onChange={set('mgmtPct')} /></label>
              <label className="text-xs text-muted">Margen %<input type="number" min="0" step="1" className={`${field} mt-1`} value={f.marginPct} onChange={set('marginPct')} /></label>
            </div>
            {preview?.billing === 'additional' && (
              <label className="text-xs text-muted">Tarifa de excedente / proyecto ({d.currency}/h){d.rateSource === 'sale' ? ' · el proyecto no tiene tarifa de excedente: se tomó la de venta' : ''}
                <input type="number" min="0" step="0.01" required className={`${field} mt-1`} value={f.rate} onChange={set('rate')} />
              </label>
            )}
            <label className="text-xs text-muted">Nota para el cliente (alcance, supuestos)
              <textarea rows={3} className={`${area} mt-1`} value={f.clientNotes} onChange={set('clientNotes')} />
            </label>
            {preview && d.estimate && (() => {
              const preliminary = billingFor(d.estimate.hours, d.sizing);
              const changed = preview.projectSized ? 'project' : preview.billing !== preliminary ? preview.billing : null;
              return (
                <div className="flex flex-col gap-1.5">
                  {changed && (
                    <p className="rounded-md bg-[#FFF4E5] px-3 py-2 text-xs font-semibold text-[#6A3A00]">
                      {changed === 'project' ? `Con este riesgo el subtotal pasa de ${d.sizing.reqMaxHours} h: es un proyecto (conviértelo)`
                        : changed === 'additional' ? 'Con este riesgo pasa a facturación adicional' : 'Con este riesgo queda dentro del contrato'}
                    </p>
                  )}
                  <p className="rounded-md bg-paper px-3 py-2 text-xs leading-relaxed">
                    Consultor {d.estimate.hours} h → subtotal con riesgo <strong>{preview.subtotalHours} h</strong> + gestión {preview.mgmtHours} h
                    {' '}= el cliente verá <strong>{preview.hours} h</strong>
                    {preview.price !== null ? <> · <strong>{money(preview.price, d.currency)}</strong> (facturación adicional)</> : ' · se descuentan del contrato'}.
                  </p>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <button type="submit" className={btnPrimary} disabled={send.isPending || Boolean(preview?.projectSized) || (preview?.billing === 'additional' && !f.rate)}>Enviar al cliente</button>
              <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancelar</button>
            </div>
          </form>
        )
      )}
      {!canQuote && !q && <p className="mt-1 text-xs text-muted">Se cotiza cuando el consultor registra su estimado.</p>}
    </div>
  );
}
